<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CardsAPI from 'custom/dashboard/api/cards';

// The agency opening a card by hand: a title and, optionally, the customer. The forms are the
// customer's way in; an agent asks for nothing else, the rest is filled in on the card.
const props = defineProps({
  // The board on screen; without one the card opens on Chamados.
  pipelineId: { type: Number, default: null },
  // The conversation's display id when the card is opened from it: the card joins it.
  conversationId: { type: Number, default: null },
  // The conversation's contact, picked to start with: { id, name, phone_number, email }.
  contact: { type: Object, default: null },
});

const emit = defineEmits(['created', 'close']);

const { t } = useI18n();

// ComboBox needs a truthy value for every option.
const NOBODY = 'none';

const dialogRef = ref(null);
const title = ref('');
const contactId = ref(NOBODY);
const results = ref([]);
const picked = ref(null);
const isSaving = ref(false);
let contactRequest = 0;

const describe = contact =>
  [contact.name, contact.phone_number || contact.email]
    .filter(Boolean)
    .join(' · ');

// The picked contact stays an option while another search runs, so the box keeps its label.
const contactOptions = computed(() => [
  { value: NOBODY, label: t('CARDS.NEW_CARD.NO_CONTACT') },
  ...[picked.value, ...results.value]
    .filter(
      (contact, index, list) =>
        contact && list.findIndex(item => item?.id === contact.id) === index
    )
    .map(contact => ({ value: contact.id, label: describe(contact) })),
]);

const pickContact = id => {
  contactId.value = id;
  picked.value =
    [picked.value, ...results.value].find(contact => contact?.id === id) ||
    null;
};

const loadContacts = async (query, request) => {
  if (request !== contactRequest) return;
  try {
    const { data } = await CardsAPI.getContactOptions(query);
    if (request === contactRequest) results.value = data.payload;
  } catch {
    if (request === contactRequest) useAlert(t('CARDS.NEW_CARD.CONTACT_ERROR'));
  }
};

const debouncedSearch = debounce(loadContacts, 300);
const searchContacts = query => {
  // Invalidate in-flight responses before the debounce delay starts.
  contactRequest += 1;
  debouncedSearch(query.trim(), contactRequest);
};

const close = () => {
  contactRequest += 1;
  emit('close');
};

onBeforeUnmount(() => {
  contactRequest += 1;
});

const open = () => {
  title.value = '';
  results.value = [];
  picked.value = props.contact;
  contactId.value = props.contact?.id || NOBODY;
  dialogRef.value.open();
  contactRequest += 1;
  loadContacts('', contactRequest);
};

const onConfirm = async () => {
  isSaving.value = true;
  try {
    const { data } = await CardsAPI.create({
      card: {
        name: title.value.trim(),
        pipeline_id: props.pipelineId || undefined,
        contact_ids: contactId.value === NOBODY ? undefined : [contactId.value],
        conversation_display_id: props.conversationId || undefined,
      },
    });
    dialogRef.value.close();
    emit('created', data);
  } catch (error) {
    useAlert(error.response?.data?.error || t('CARDS.NEW_CARD.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="$t('CARDS.NEW_CARD.TITLE')"
    :confirm-button-label="$t('CARDS.NEW_CARD.CREATE')"
    :disable-confirm-button="!title.trim() || isSaving"
    :is-loading="isSaving"
    @confirm="onConfirm"
    @close="close"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">{{
          $t('CARDS.NEW_CARD.NAME')
        }}</span>
        <Input
          v-model="title"
          :placeholder="$t('CARDS.NEW_CARD.NAME_PLACEHOLDER')"
          data-testid="card-new-name"
        />
      </div>
      <div class="flex flex-col gap-1" data-testid="card-new-contact">
        <span class="text-sm text-n-slate-12">{{
          $t('CARDS.NEW_CARD.CONTACT')
        }}</span>
        <ComboBox
          :model-value="contactId"
          :options="contactOptions"
          use-api-results
          :search-placeholder="$t('CARDS.NEW_CARD.CONTACT_SEARCH')"
          :empty-state="$t('CARDS.NEW_CARD.CONTACT_EMPTY')"
          @update:model-value="pickContact"
          @search="searchContacts"
        />
      </div>
    </div>
  </Dialog>
</template>
